import { MantineProvider } from '@mantine/core'
import { PropsWithChildren, useEffect } from 'react'
import tinycolor from 'tinycolor2'
import { useTranslation } from 'react-i18next'

import { LemonadeProvider } from '@percent/lemonade'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import { usePartnerConfig } from '@percent/product-nomination-form/hooks'
import GoodstackLogo from '@percent/product-nomination-form/assets/icons/goodstack-page-logo.svg'

const defaultColors = {
  primaryColor: '#EAEAFE',
}

// Exactly 10 elements required by Mantine
const fillColor = (color: string) =>
  [
    color,
    color,
    color,
    color,
    color,
    color,
    color,
    color,
    color,
    color,
  ] as const

const getPrimaryAccent = (color: string) =>
  tinycolor(color).isDark()
    ? tinycolor(color).brighten(15).toString()
    : tinycolor(color).darken(15).toString()

export function ThemeProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation()
  const { partnerConfig: nominationConfig } = usePartnerConfig()
  const partnerPrimaryColor =
    nominationConfig?.configuration.primaryColor || defaultColors.primaryColor
  const partnerSecondaryColor =
    nominationConfig?.configuration.secondaryColor || partnerPrimaryColor

  const isPartnerPrimaryColorDark = tinycolor(partnerPrimaryColor).isDark()

  useEffect(() => {
    if (nominationConfig) {
      const favicon = document.getElementById('favicon') as HTMLLinkElement
      const description = document.getElementById(
        'description'
      ) as HTMLMetaElement

      if (favicon && document && description) {
        favicon.href = nominationConfig?.configuration.logoUrl ?? GoodstackLogo
        document.title = t('hostedValidation.site.title', {
          partner: nominationConfig?.configuration.partner.name,
        })
        description.content = t('hostedValidation.site.contentApply', {
          partnerName: nominationConfig?.configuration.partner.name,
        })
      }
    }
  }, [nominationConfig, t])

  const ctaTextColor = isPartnerPrimaryColorDark ? '#FFFFFF' : '#000000'

  return (
    <MantineProvider
      theme={{
        primaryColor: 'primary',
        colors: {
          primary: fillColor(partnerPrimaryColor),
          secondary: fillColor(partnerSecondaryColor),
        },
        white: ctaTextColor,
      }}
    >
      <LemonadeProvider
        customPalette={{
          primary400: partnerPrimaryColor,
          primary600: getPrimaryAccent(partnerPrimaryColor),
          secondary400: partnerSecondaryColor,
          secondary600: getPrimaryAccent(partnerSecondaryColor),
          white: ctaTextColor,
        }}
      >
        {children}
      </LemonadeProvider>
    </MantineProvider>
  )
}
